import {
  DdlDepartment,
  DdlLevel,
  DdlQuestionDepartment,
  DdlQuestionGroups,
  DdlQuestionPosition,
  DdlQuestionPositionProps,
  DdlQuestionTypes,
  DdlQuestionUnit,
  DdlQuestionUsers,
  DdlRegion,
  IConditionsProps,
  ICreateOneAndManyAnswer,
  ICreateOneAndManyAnswerAddResponse,
  ICreateQuestionMatrix,
  ICreateQuestionMatrixList,
  ICreateQuestionNoAnswer,
  ICreateQuestionRating,
  IDetailQuestionBank,
  IList,
  IRepFileOneAnswer,
  IRequestSearch,
  IRequestSearchList,
  ISubmitAnswerRating,
  IDDLMemberGroupProps,
  IDDLRegionGroupResponse,
  IDDLUnitGroupResponse,
  IDDLDepartmentGroupResponse,
  IDDLDivisionGroupResponse,
  IDDLRestaurantGroupResponse,
  IDDLPositionGroupResponse,
  IDDLevelGroupResponse,
  IDepartmentRequest,
  IRegionRequest,
  IUnitRequest,
  IDivisionRequest,
  ILevelRequest,
} from 'views/pages/questionBank/type';
import { CombineTypeAnswer } from 'views/pages/questionBank/questionAdd/types';
import {
  DdlGroupDivision,
  DdlGroupRestaurant,
  DdlGroupRestaurantProps,
} from 'views/pages/survey/surveyAdd/ListOfMembers/types';
import { ICheckFileBeforeUpload } from 'views/pages/survey/type';

import ApiClient from './ApiClient';
import { getInfoUserAuth, handleGetUSerID } from 'utils/helperFunctions';

export const getQuestionsApi = async (params: IRequestSearch): Promise<IList> => {
  const { data } = await ApiClient.postJsonData('/Questions', {}, { ...params });
  return data;
};
export const getQuestionsListApi = async (
  params: IRequestSearchList,
  controller?: AbortController
): Promise<IList> => {
  const { data } = await ApiClient.postJsonData(
    '/Questions',
    {},
    { ...params },
    { ...(controller ? { signal: controller?.signal } : {}) }
  );
  return data;
};
export const getQuestionsBranchingApi = async (surveyID: string): Promise<IConditionsProps[]> => {
  const { data } = await ApiClient.get(
    '/Survey/getSurveyOneAnswerQuestions',
    {
      surveyID: surveyID,
    },
    {}
  );
  return data;
};
export const getDDLQuestionTypesApi = async (
  isQuestionBank = false
): Promise<DdlQuestionTypes[]> => {
  const { data } = await ApiClient.get('/DropDownList/ddlQuestionType', {}, { isQuestionBank });
  return data;
};
export const getDDLQuestionGroupsApi = async (): Promise<DdlQuestionGroups[]> => {
  const { data } = await ApiClient.get('/DropDownList/ddlQuestionGroup', {});
  return data;
};
export const getDDLQuestionRegionApi = async (params: IRegionRequest): Promise<DdlRegion[]> => {
  const paramRegions = params.selectRegionIDs;
  const { data } = await ApiClient.postJsonData('/DropDownList/ddlRegion', {}, paramRegions);
  return data;
};
export const getDDLDepartmentApi = async (
  params: IDepartmentRequest
): Promise<DdlQuestionDepartment[]> => {
  const { data } = await ApiClient.postJsonData('/DropDownList/ddlDepartment', {}, params);
  return data;
};
export const getDDLQuestionUsersApi = async (): Promise<DdlQuestionUsers[]> => {
  const { data } = await ApiClient.get('/DropDownList/ddlUserGroup', {});
  return data;
};
export const getDDLQuestionUnitsApi = async (params: IUnitRequest): Promise<DdlQuestionUnit[]> => {
  const { data } = await ApiClient.postJsonData('/DropDownList/ddlUnit', {}, params);
  return data;
};
export const getDDLQuestionDepartmentApi = async (
  params: IDepartmentRequest
): Promise<DdlQuestionDepartment[]> => {
  const { data } = await ApiClient.postJsonData('/DropDownList/ddlDepartment', {}, params);
  return data;
};

export const getDDLDivisionApi = async (params: IDivisionRequest): Promise<DdlGroupDivision[]> => {
  const { data } = await ApiClient.postJsonData('/DropDownList/ddlDivision', {}, params);
  return data;
};

export const getDDLRestaurantApi = async ({
  page = 1,
  pageSize = 20,
  restaurantName = '',
  divisions = [],
  extraConfig,
  surveyID = null,
  restaurantDtos = [],
}: Partial<DdlGroupRestaurantProps>): Promise<DdlGroupRestaurant[]> => {
  let params = {};
  if (surveyID) {
    params = {
      page,
      pageSize,
      restaurantName,
      divisions,
      surveyID,
      restaurantDtos,
    };
  } else {
    params = {
      page,
      pageSize,
      restaurantName,
      divisions,
      restaurantDtos,
    };
  }
  const { data } = await ApiClient.postJsonData(
    '/DropDownList/ddlRestaurant',
    {},
    { ...params },
    extraConfig
  );
  return data;
};

export const getDDLQuestionPositionsApi = async ({
  page = 1,
  pageSize = 10,
  positionName = '',
  surveyID = null,
  positionDtos = [],
}: Partial<DdlQuestionPositionProps>): Promise<DdlQuestionPosition[]> => {
  let params = {};
  if (surveyID) {
    params = {
      page,
      pageSize,
      positionName,
      surveyID,
      positionDtos,
    };
  } else {
    params = {
      page,
      pageSize,
      positionName,
      positionDtos,
    };
  }
  const { data } = await ApiClient.postJsonData(
    '/DropDownList/ddlPosition',
    {},
    {
      ...params,
    }
  );
  return data;
};
export const getDDLQuestionLevelsApi = async (params: ILevelRequest): Promise<DdlLevel[]> => {
  const paramLevel = params.selectLevelIDs;
  const { data } = await ApiClient.postJsonData(
    '/DropDownList/ddlLevel',
    {
      surveyID: params.surveyID,
    },
    paramLevel
  );
  return data;
};

export const getDDLRegionGroupApi = async (
  params: IDDLMemberGroupProps
): Promise<IDDLRegionGroupResponse> => {
  const { data } = await ApiClient.postJsonData('/DropDownList/ddlRegionGroup', {}, params);
  return data;
};

export const getDDLUnitGroupApi = async (
  params: IDDLMemberGroupProps
): Promise<IDDLUnitGroupResponse> => {
  const { data } = await ApiClient.postJsonData('/DropDownList/ddlUnitGroup', {}, params);
  return data;
};

export const getDDLDepartmentGroupApi = async (
  params: IDDLMemberGroupProps
): Promise<IDDLDepartmentGroupResponse> => {
  const { data } = await ApiClient.postJsonData('/DropDownList/ddlDepartmentGroup', {}, params);
  return data;
};

export const getDDLDivisionGroupApi = async (
  params: IDDLMemberGroupProps
): Promise<IDDLDivisionGroupResponse> => {
  const { data } = await ApiClient.postJsonData('/DropDownList/ddlDivisionGroup', {}, params);
  return data;
};

export const getDDLRestaurantGroupApi = async (
  params: IDDLMemberGroupProps
): Promise<IDDLRestaurantGroupResponse> => {
  const { data } = await ApiClient.postJsonData('/DropDownList/ddlRestaurantGroup', {}, params);
  return data;
};

export const getDDLPositionGroupApi = async (
  params: IDDLMemberGroupProps
): Promise<IDDLPositionGroupResponse> => {
  const { data } = await ApiClient.postJsonData('/DropDownList/ddlPositionGroup', {}, params);
  return data;
};

export const getDDLLevelGroupApi = async (
  params: IDDLMemberGroupProps
): Promise<IDDLevelGroupResponse> => {
  const { data } = await ApiClient.postJsonData('/DropDownList/ddlLevelGroup', {}, params);
  return data;
};

export const createOneAndManyAnswerApi = async (
  dataQuestion: ICreateOneAndManyAnswer,
  userID: string,
  isQuestionBank: boolean
): Promise<ICreateOneAndManyAnswerAddResponse> => {
  const { data } = await ApiClient.postJsonData(
    '/Questions/createOneAndManyAnswer',
    {},
    { ...dataQuestion }
  );
  return data;
};

export const createEvaluatedQuestionApi = async (surveyID: string): Promise<string> => {
  const { data } = await ApiClient.postJsonData('/Questions/createQuestion360', {}, { surveyID });
  return data;
};

export const updateOneAndManyAnswerApi = async (
  dataQuestion: ICreateOneAndManyAnswer,
  userID: string,
  isQuestionBank: boolean
): Promise<ICreateOneAndManyAnswer> => {
  const { data } = await ApiClient.putJsonData(
    '/Questions/updateOneAndManyAnswer',
    { userIdUpdate: userID, isQuestionBank },
    { ...dataQuestion }
  );
  return data;
};

export const createFileOneAnswerApi = async (
  params: {
    questionImage?: File;
    answerImages?: Array<File>;
  },
  isMatrix?: boolean
): Promise<IRepFileOneAnswer> => {
  const { data } = await ApiClient.postMutipartData(
    '/Image',
    { isMatrix: Boolean(isMatrix) },
    { ...params }
  );
  return data;
};

export const createTextNumberDateTimeQuestionApi = async (
  dataQuestion: ICreateQuestionNoAnswer,
  userID: string,
  isQuestionBank: boolean
): Promise<string> => {
  const { data } = await ApiClient.postJsonData(
    '/Questions/createQuestionNoAnswer',
    {},
    dataQuestion
  );
  return data;
};

export const updateNoAnswerQuestion = async (
  dataQuestion: ICreateQuestionNoAnswer,
  userID: string,
  isQuestionBank: boolean
): Promise<ICreateQuestionNoAnswer> => {
  const { data } = await ApiClient.putJsonData(
    '/Questions/updateQuestionNoAnswer',
    { userIdUpdate: userID, isQuestionBank },
    dataQuestion
  );
  return data;
};

export const createQuestionRatingApi = async (
  dataQuestion: ICreateQuestionRating,
  userID: string,
  isQuestionBank: boolean
): Promise<string> => {
  const { data } = await ApiClient.postJsonData(
    '/Questions/createQuestionRating',
    {},
    dataQuestion
  );
  return data;
};

export const updateQuestionRatingApi = async (
  dataQuestion: ICreateQuestionRating,
  userID: string,
  isQuestionBank: boolean
): Promise<ICreateQuestionRating> => {
  const { data } = await ApiClient.putJsonData(
    '/Questions/updateQuestionsRating',
    { userIdUpdate: userID, isQuestionBank },
    dataQuestion
  );
  return data;
};

export const createMaxtrixQuestionApi = async (
  dataQuestion: ICreateQuestionMatrix,
  userID: string,
  isQuestionBank: boolean
): Promise<string> => {
  const { data } = await ApiClient.postJsonData(
    '/Questions/createQuestionMatrix',
    {},
    dataQuestion
  );
  return data;
};

export const updateQuestionMartrix = async (
  dataQuestion: ICreateQuestionMatrix,
  userID: string,
  isQuestionBank: boolean
): Promise<ICreateQuestionMatrix> => {
  const { data } = await ApiClient.putJsonData(
    '/Questions/updateQuestionMatrix',
    { userIdUpdate: userID, isQuestionBank },
    dataQuestion
  );
  return data;
};

export const createMaxtrixFormListQuestionApi = async (
  dataQuestion: ICreateQuestionMatrixList,
  userID: string,
  isQuestionBank: boolean
): Promise<string> => {
  const { data } = await ApiClient.postJsonData(
    '/Questions/createQuestionMatrixList',
    {},
    dataQuestion
  );

  return data;
};

export const updateMaxtrixFormListQuestionApi = async (
  dataQuestion: ICreateQuestionMatrixList,
  userID: string,
  isQuestionBank: boolean
): Promise<ICreateQuestionMatrixList> => {
  const { data } = await ApiClient.putJsonData(
    '/Questions/updateQuestionMatrixList',
    { userIdUpdate: userID, isQuestionBank },
    dataQuestion
  );

  return data;
};

export const fetchDetailQuestionBank = async (params: {
  questionID: string;
}): Promise<IDetailQuestionBank | any> => {
  const { data } = await ApiClient.get('/Questions/getQuestionByID', {}, { ...params });
  return data;
};

export const deleteById = async (questionID: string): Promise<any> => {
  const params = {
    questionID,
  };
  const response = await ApiClient.delete('/Questions/deleteQuestion', params);
  return response.data;
};

export const checkExistQuestionByContentApi = async (params: {
  questionContent: string;
  questionID?: string;
}): Promise<boolean> => {
  const { data } = await ApiClient.get('/Questions/checkExistQuestionByContent', {}, { ...params });
  return data;
};
export const getDownloadExcel = async (fileName: string, type: number): Promise<boolean> => {
  const { data } = await ApiClient.downloadExcelGetXlsm(
    '/Questions/exportQuestionTemplate',
    {},
    { type },
    fileName
  );
  return data;
};

export const checkFileBeforeUpload = async (formData: any): Promise<ICheckFileBeforeUpload[]> => {
  const { data } = await ApiClient.uploadFileExcelPostMutipartData(
    '/Questions/checkFileBeforeUpload',
    {},
    formData
  );
  return data;
};

export const uploadQuestionsApi = async (formData: any, option: any, dataFromAPI?: any) => {
  const { userID } = getInfoUserAuth();
  formData.append('jsonSheet', dataFromAPI ? JSON.stringify(dataFromAPI) : '');
  const { data } = await ApiClient.uploadFileExcelPostMutipartData(
    '/Questions/uploadQuestions',
    { userID },
    formData,
    option
  );
  return data;
};

export const copyQuestionApi = async (
  questionID: string,
  userID: string
): Promise<CombineTypeAnswer> => {
  const { data } = await ApiClient.postJsonData(
    '/Questions/copyQuestion',
    { questionID, userID },
    {}
  );
  return data;
};

export const checkQuestionInSurvey = async (
  questionID: string,
  surveyID?: string
): Promise<boolean> => {
  let params;
  if (surveyID) {
    params = {
      questionID,
      surveyID,
    };
  } else {
    params = {
      questionID,
    };
  }

  const { data } = await ApiClient.get('/Questions/checkQuestionInSurvey', {}, { ...params });
  return data;
};

export const CheckQuestion360Exist = async ({
  questionID,
  surveyID,
}: {
  questionID: string;
  surveyID: string;
}): Promise<boolean> => {
  const params = { questionID, surveyID };
  const { data } = await ApiClient.get('/Questions/CheckQuestion360Exist', {}, { ...params });
  return data;
};

export const deleteQuestion360 = async ({
  questionID,
  surveyID,
}: {
  questionID: string;
  surveyID: string;
}): Promise<boolean> => {
  const params = { questionID, surveyID };
  const { data } = await ApiClient.delete('/Questions/deleteQuestion360', params);
  return data;
};
